import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { roleLabel } from '../lib/roles'
import { User, Mail, Shield, Save, KeyRound } from 'lucide-react'

export function Profile() {
  const { profile } = useAuth()
  const [name, setName] = useState(profile?.name || '')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [searchParams] = useSearchParams()
  const welcome = searchParams.get('welcome') === '1'
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwMessage, setPwMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPwMessage(null)
    if (password.length < 8) {
      setPwMessage({ ok: false, text: 'Use at least 8 characters.' })
      return
    }
    if (password !== password2) {
      setPwMessage({ ok: false, text: "The two passwords don't match." })
      return
    }
    setPwSaving(true)
    const { error } = await supabase.auth.updateUser({ password })
    setPwSaving(false)
    if (error) {
      setPwMessage({ ok: false, text: error.message })
    } else {
      setPassword('')
      setPassword2('')
      setPwMessage({ ok: true, text: 'Password saved. You can now sign in with your email and this password.' })
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage('')

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ name })
        .eq('id', profile?.id)

      if (error) throw error

      setMessage('Profile updated successfully!')
      setTimeout(() => setMessage(''), 3000)
    } catch (error) {
      console.error('Error updating profile:', error)
      setMessage('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {welcome && (
        <div className="mb-6 p-4 rounded-lg bg-brand-400/10 border border-brand-400/40 text-sm text-gray-800 dark:text-gray-200">
          <p className="font-semibold">Welcome to MyTutor+.</p>
          <p className="mt-1">You signed in with an email link. Set a password below so you can sign in normally next time.</p>
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Profile</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage your account information
        </p>
      </div>

      <div className="bg-white dark:bg-surface-100 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6">
          <div className="flex items-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center flex-shrink-0">
              <User className="w-10 h-10 text-white" />
            </div>
            <div className="ml-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {profile?.name || profile?.email?.split('@')[0] || 'User'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {roleLabel(profile?.role)}
              </p>
            </div>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  Name
                </div>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </div>
              </label>
              <input
                type="email"
                value={profile?.email || ''}
                disabled
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Email cannot be changed
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center">
                  <Shield className="w-4 h-4 mr-2" />
                  Role
                </div>
              </label>
              <input
                type="text"
                value={roleLabel(profile?.role)}
                disabled
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Only a super admin can change roles.</p>
            </div>

            {message && (
              <div className={`p-4 rounded-lg ${
                message.includes('success')
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-400'
              }`}>
                {message}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="flex items-center px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-6 bg-white dark:bg-surface-100 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <form onSubmit={handlePassword} className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <KeyRound className="w-5 h-5" /> {welcome ? 'Set your password' : 'Change password'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (8+ characters)"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
            />
            <input
              type="password"
              autoComplete="new-password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              placeholder="Type it again"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
            />
          </div>
          {pwMessage && (
            <p className={`text-sm ${pwMessage.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{pwMessage.text}</p>
          )}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={pwSaving || !password}
              className="flex items-center px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <KeyRound className="w-4 h-4 mr-2" />
              {pwSaving ? 'Saving...' : 'Save password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}